import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ErrorResponse, Spinner, Toolbar } from '~components';
import Icon, { faPlus } from '~components/icon';

import { CarePlan, CarePlanLookup } from '~clients/models';

import {
  useGetCarePlanReviewsQuery,
  useGetReviewTriggerTypesQuery,
  useGetReviewTypesQuery,
} from '~clients/actions/carePlans';

import { getAll as getUsers } from '~care-workers/actions';

import { selectLookupUsers } from '~care-workers/selectors/users';

import NextReviewBanner from './NextReviewBanner';
import ReviewFormModal from './ReviewFormModal';
import ReviewTimeline from './ReviewTimeline';

interface ComponentProps {
  plan: CarePlan;
}

const byId = (rows: CarePlanLookup[]): Map<string, CarePlanLookup> => new Map(rows.map((row) => [row.id, row]));

/**
 * Reviews tab — a next-review-due banner, an "Add Review" modal, and a chronological
 * (newest-first) timeline of the plan's reviews. Recording a review updates the plan's
 * review_due_date server-side, refreshing the banner.
 */
const CarePlanReviewsTab: React.FC<ComponentProps> = ({ plan }) => {
  const dispatch = useAppDispatch();
  const { data: reviews, isLoading, error } = useGetCarePlanReviewsQuery({ planId: plan.id });
  const { data: reviewTypes } = useGetReviewTypesQuery();
  const { data: triggerTypes } = useGetReviewTriggerTypesQuery();
  const users = useAppSelector(selectLookupUsers);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reviewer names for the timeline (and options for the modal's reviewer select).
  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const reviewTypesById = useMemo(() => byId(reviewTypes ?? []), [reviewTypes]);
  const triggerTypesById = useMemo(() => byId(triggerTypes ?? []), [triggerTypes]);
  const usersById = useMemo(() => new Map(users.map(({ id, name }) => [id, name])), [users]);

  const list = reviews ?? [];

  return (
    <div className="section-card-container" data-cy="care-plan-reviews">
      <NextReviewBanner reviewDueDate={plan.review_due_date} />

      <Toolbar
        id="care-plan-reviews"
        rightCluster={
          <Button size="sm" color="success" dataCy="care-plan-review-add" onClick={() => setIsModalOpen(true)}>
            <Icon className="me-2" icon={faPlus} />
            New Review
          </Button>
        }
      />

      {isLoading && (
        <div className="care-plan-section-loading">
          <Spinner loading />
        </div>
      )}
      {error !== undefined && <ErrorResponse dataCy="care-plan-reviews-error" message={getErrorMessage(error)} />}
      {!isLoading && error === undefined && list.length === 0 && (
        <p className="text-muted care-plan-reviews-empty" data-cy="care-plan-reviews-empty">
          No reviews recorded.
        </p>
      )}
      {!isLoading && error === undefined && list.length > 0 && (
        <ReviewTimeline
          reviews={list}
          reviewTypesById={reviewTypesById}
          triggerTypesById={triggerTypesById}
          usersById={usersById}
        />
      )}

      <ReviewFormModal planId={plan.id} isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </div>
  );
};

export default CarePlanReviewsTab;
